// MUI
import { createTheme, Card, CardActions, CardContent, Typography, Box, Modal, Button, Backdrop, Fade, TextField, IconButton } from '@mui/material';
import { DeleteOutline, Check, Edit } from '@mui/icons-material';

// Contexts
import { TodosContext } from '../contexts/TodosProvider';

// Hooks
import { useState, useMemo, useContext } from 'react';

// Components
import SnackbarComp from './SnackbarComp';

export default function Todos() {

    const theme = createTheme();

    // from context
    const { todos, dispatch, filteration } = useContext(TodosContext);

    // notification if a todo is added, edited or deleted
    const [clickedBtn, setClickedBtn] = useState("");
    const [open, setOpen] = useState(false);

    // modal (pop up) styling
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: "220px", sm: "400px" },
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = (reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    // edited ( or deleted ) todo ID
    const [selectedTodoId, setSelectedTodoId] = useState(null);

    // todo title & details
    const [currentTitle, setCurrentTitle] = useState("");
    const [currentDetails, setCurrentDetails] = useState("");

    // show "delete" modal & close it
    const [openDelete, setOpenDelete] = useState(false);
    const handleOpenDelete = (currentId) => {
        setSelectedTodoId(currentId);
        setOpenDelete(true);
    }
    const handleCloseDelete = () => setOpenDelete(false);

    function handleDelete(deletedID) {
        dispatch({ type: "delete", payload: { newID: deletedID } });
        setOpenDelete(false);
        setClickedBtn("delete");
        handleClick();
    }

    // show "edit" modal & close it
    const [openUpdate, setOpenUpdate] = useState(false);
    const handleOpenUpdate = (currentId, title, details) => {
        setSelectedTodoId(currentId);
        setCurrentTitle(title);
        setCurrentDetails(details);
        setOpenUpdate(true);
    }
    
    const handleCloseUpdate = () => setOpenUpdate(false);

    function handleUpdate(updatedID) {
        if (currentTitle.trim() === "" && currentDetails.trim() === "") {
            setOpenUpdate(false);
        } else {
            dispatch({ type: "update", payload: { newTitle: currentTitle, newDetails: currentDetails, newID: updatedID } })
            setOpenUpdate(false);
            setClickedBtn("update");
            handleClick();
        }
    }

    // todos ui
    function renderTodo(todo) {

        // completed or not-completed todo
        function handleCheck(checkedID, completedStatus) {
            dispatch({ type: "check", payload: { newID: checkedID } });
            async function handleWaiting() {
                await new Promise(res => setTimeout(() => {
                    setOpen(false);
                    res();
                }, 100)).then(() => {
                    setClickedBtn(completedStatus === false ? "check" : "uncheck");
                    handleClick();
                })
            }
            handleWaiting();
        }

        return (
            <Card key={todo.id} className="todoCard"
                sx={{
                    backgroundColor: theme.palette.primary.dark, color: theme.palette.primary.contrastText
                    , display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0px 6px 10px -3px black",
                    borderRadius: "10px", flexDirection: { xs: "column", md: "row" },
                    width: "100%",
                }}>

                {/* todo title & details */}

                <CardContent sx={{ maxWidth: "350px", overflowWrap: "anywhere" }}>
                    <Typography gutterBottom variant="h5"
                        sx={{
                            fontWeight: "bold", maxWidth: "300px", overflowWrap: "anywhere",
                            fontSize: { xs: "1.25rem", md: "1.5rem" }, textDecoration: todo.isCompleted ? "line-through" : "none",
                            opacity: todo.isCompleted ? "0.8" : "1",
                        }}>
                        {todo.title}
                    </Typography>
                    <Typography variant="h6" component="div"
                        sx={{
                            fontWeight: "bold", maxWidth: "350px", overflowWrap: "anywhere",
                            whiteSpace: "normal",
                            color: "#c5c5c5",
                            fontSize: { xs: "1rem", md: "1.25rem" }, textDecoration: todo.isCompleted ? "line-through" : "none",
                            opacity: todo.isCompleted ? "0.8" : "1",
                        }}>
                        {todo.details}
                    </Typography>
                </CardContent>

                {/* check, edit & delete buttons */}

                <CardActions>
                    <IconButton onClick={() => {
                        handleCheck(todo.id, todo.isCompleted);
                    }} className="iconbtn" aria-label="delete" size="medium"
                        sx={{
                            border: `3px solid ${theme.palette.success.light}`,
                            color: (todo.isCompleted ? "white" : theme.palette.success.light),
                            backgroundColor: (todo.isCompleted ? theme.palette.success.light : "white"),
                            "&:hover": {
                                backgroundColor: (todo.isCompleted) ? theme.palette.success.light : "white",
                            }
                        }}>
                        <Check fontSize="inherit" />
                    </IconButton>
                    <IconButton onClick={() => {
                        handleOpenUpdate(todo.id, todo.title, todo.details);
                    }} className="iconbtn" aria-label="delete" size="medium"
                        sx={{
                            border: `3px solid ${theme.palette.info.light}`,
                            color: theme.palette.info.light,
                            backgroundColor: "white"
                        }}>
                        <Edit fontSize="inherit" />
                    </IconButton>
                    <IconButton onClick={() => { handleOpenDelete(todo.id) }} className="iconbtn" aria-label="delete" size="medium" sx={{
                        border: `3px solid ${theme.palette.error.main}`,
                        color: theme.palette.error.main,
                        backgroundColor: "white"
                    }}>
                        <DeleteOutline fontSize="inherit" />
                    </IconButton>
                </CardActions>
            </Card>
        )
    }

    // all todos
    const todosUI = useMemo(() => {
        return todos.map((todo) => {
            return renderTodo(todo);
        })
    }, [todos]);

    // completed todos
    const completedTodosUI = useMemo(() => {
        return todos.filter((todo) => {
            return todo.isCompleted;
        }).map((todo) => {
            return renderTodo(todo);
        })
    }, [todos])

    // not completed todos
    const notCompletedTodosUI = useMemo(() => {
        return todos.filter((todo) => {
            return !todo.isCompleted;
        }).map((todo) => {
            return renderTodo(todo);
        })
    }, [todos])

    return (
        <Box
            sx={{
                display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "30px",
            }}>

            {/* main content */}

            {
                (filteration === "all") ? todosUI
                    :
                    (filteration === "completed") ? completedTodosUI
                        :
                        notCompletedTodosUI
            }

            {/* edit & delete (modals) */}
            <Modal
                open={openUpdate}
                onClose={handleCloseUpdate}
                closeAfterTransition
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={openUpdate}>
                    <Box sx={style}>
                        <Typography variant="h4" color="inherit">تعديل المهمة</Typography>
                        <TextField value={currentTitle}
                            sx={{ width: "100%", marginTop: "30px" }} label="عنوان المهمة" variant="standard"
                            onChange={(e) => {
                                setCurrentTitle(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleUpdate(selectedTodoId);
                                }
                            }}
                        />
                        <TextField value={currentDetails}
                            sx={{ width: "100%", marginTop: "30px" }} label="تفاصيل المهمة" variant="standard"
                            onChange={(e) => {
                                setCurrentDetails(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleUpdate(selectedTodoId);
                                }
                            }}
                        />
                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <Button variant="text" sx={{ fontSize: "20px" }} onClick={() => {
                                setOpenUpdate(false);
                            }}>إغلاق
                            </Button>
                            <Button variant="text" sx={{ fontSize: "20px" }} onClick={() => handleUpdate(selectedTodoId)}>تعديل</Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
            <Modal
                open={openDelete}
                onClose={handleCloseDelete}
                closeAfterTransition
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={openDelete}>
                    <Box sx={style}>
                        <Typography id="modal-modal-title" variant="h5" component="h2">
                            هل أنت متأكد من حذف المهمة ؟
                        </Typography>
                        <Typography id="modal-modal-description" sx={{ mt: 2, color: "#777" }}>
                            لا يمكنك التراجع عن الحذف بعد إتمامه
                        </Typography>
                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <Button variant="text" sx={{ fontSize: "20px" }} onClick={() => {
                                handleCloseDelete();
                            }}>إغلاق</Button>
                            <Button variant="text" sx={{ fontSize: "20px" }} onClick={() => {
                                handleDelete(selectedTodoId);
                            }}>حذف</Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
            <SnackbarComp
                clickedBtn={clickedBtn}
                handleClose={handleClose}
                open={open} />
        </Box>
    )
}
